import type { PostMatchInsight } from '../../../shared/whatsapp/insights';
import { getCategory } from '../../../shared/whatsapp/postMatchSummary';

export type ReportGroupKey = 'headlines' | 'streaks' | 'together' | 'records';

export const REPORT_GROUP_LABELS: Record<ReportGroupKey, string> = {
  headlines: 'Headlines',
  streaks: 'Streaks & milestones',
  together: 'Partnerships & rivalries',
  records: 'Records & trophies',
};

const STREAK_CATEGORIES = new Set(['streak', 'attendance', 'cap', 'team_streak', 'return', 'debut', 'injury_return', 'bench_warmer']);
const TOGETHER_CATEGORIES = new Set([
  'rivalry_first_win',
  'rivalry_perfect',
  'rivalry_other',
  'partnership_first',
  'partnership_milestone',
  'chemistry_duo',
  'chemistry_milestone',
  'trio',
]);

/** Up to this many priority-1 insights lead the report (as in the classic insights list). */
export const MAX_HEADLINES = 3;

/** Which kind section an insight belongs to (headlines are picked separately). */
export function reportGroupOf(insight: Pick<PostMatchInsight, 'analysisType'>): Exclude<ReportGroupKey, 'headlines'> {
  const category = getCategory(insight.analysisType);
  if (STREAK_CATEGORIES.has(category)) return 'streaks';
  if (TOGETHER_CATEGORIES.has(category) || insight.analysisType.includes('chemistry') || insight.analysisType.includes('rivalry')) return 'together';
  return 'records';
}

export interface ReportGroup {
  key: ReportGroupKey;
  label: string;
  insights: PostMatchInsight[];
}

/**
 * Insights split into report sections (fixed order, empty ones dropped), each
 * sorted by priority. The first few priority-1 insights are the headlines; the
 * rest go to their kind's section.
 */
export function groupReportInsights(insights: PostMatchInsight[]): ReportGroup[] {
  const order: ReportGroupKey[] = ['headlines', 'streaks', 'together', 'records'];
  const byKey = new Map<ReportGroupKey, PostMatchInsight[]>(order.map((k) => [k, []]));
  // Priority-1 insights tie on created_at, so a first-ever win is moved up to always make the cut
  const isFirstEverWin = (i: PostMatchInsight) => i.analysisType === 'first_ever_win_nemesis';
  const leads = insights.filter((i) => i.priority === 1).sort((a, b) => Number(isFirstEverWin(b)) - Number(isFirstEverWin(a)));
  const headlines = new Set(leads.slice(0, MAX_HEADLINES));
  for (const insight of insights) byKey.get(headlines.has(insight) ? 'headlines' : reportGroupOf(insight))!.push(insight);
  return order
    .map((key) => ({
      key,
      label: REPORT_GROUP_LABELS[key],
      insights: [...byKey.get(key)!].sort((a, b) => a.priority - b.priority),
    }))
    .filter((g) => g.insights.length > 0);
}
